import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild, inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { ThemeService } from '../../services/theme.service';
import { chartColors, chartPalette } from './chart-theme';

// ── Public interfaces exported for use in page / dashboard components ──────────

export interface BreakdownClientRow {
  /** Real contact ID — use for filter logic, never clientName */
  contactId: string | null;
  clientName: string;
  revenue: number;
  /** Pre-formatted currency string */
  amount: string;
  /** 0–100 */
  percentage: number;
  /** Pre-formatted '28.9%' */
  percentageLabel: string;
  /** 0–100 proportional bar width */
  barWidth: number;
  /** Palette color for this rank slot */
  color: string;
  /** true when this client is the active cross-filter selection */
  isSelected: boolean;
  /** true when client exceeds concentration risk threshold */
  isConcentrationRisk: boolean;
  /** signed delta vs prior period; null = no prior data */
  delta: number | null;
  /** formatted delta string; null = hide */
  deltaLabel: string | null;
}

export interface BreakdownViewModel {
  clients: BreakdownClientRow[];
  totalRevenue: number;
  totalRevenueLabel: string;
  /** e.g. 'Jun 2026', 'Q2 2025', 'All Time' */
  periodLabel: string;
  /** null = no concentration risk data available */
  concentrationThreshold: number | null;
  /** index of hovered row driven by chart; null = none */
  hoveredIndex: number | null;
}

export interface RadialChartDetail {
  amount: string;
  percentage: string;
}

export interface RadialSegmentClick {
  index: number;
  nativeEvent: MouseEvent;
}

// ── Deterministic rank-based palette — deep jewel tones, matches the
//    --color-chart-series-* tokens in styles.scss (light mode) ─────────────
export const BREAKDOWN_PALETTE = [
  '#0F6B62', // rank 0 — deep teal
  '#1E3A6E', // rank 1 — deep indigo
  '#92400E', // rank 2 — deep amber
  '#7C2D3E', // rank 3 — deep wine
  '#4C1D95', // rank 4 — deep violet
  '#155E75', // rank 5 — deep cyan
  '#14532D', // rank 6 — deep forest
  '#831843', // rank 7 — deep magenta
  '#44403C', // rank 8 — deep stone
  '#9A3412', // rank 9 — deep rust
];

export function breakdownPalette(): string[] {
  return document.documentElement.classList.contains('dark') ? chartPalette() : BREAKDOWN_PALETTE;
}
@Component({
  selector: 'app-revenue-radial-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block w-full' },
  templateUrl: './revenue-radial-chart.component.html',
})
export class RevenueRadialChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // Legacy compat
  @Input() data: any = null;
  @Input() selectedClient: string | null = null;
  @Input() centerValue = '';
  @Input() centerLabel = '';

  // New rich view model
  @Input() viewModel: BreakdownViewModel | null = null;
  @Input() periodLabel = '';

  @Output() segmentClick = new EventEmitter<RadialSegmentClick>();
  @Output() segmentContextMenu = new EventEmitter<RadialSegmentClick>();
  @Output() segmentHoverIndex = new EventEmitter<number | null>();

  @ViewChild('chartContainer', { static: true })
  private chartContainer?: ElementRef<HTMLElement>;

  options: EChartsOption | null = null;

  /** ECharts instance — obtained via (chartInit) so we can call setOption directly. */
  private echartsInstance: any = null;

  /**
   * Tracks the previous data-segment count.
   * When the count changes (1→10 on drill-up, 10→1 on drill-down) we force a
   * notMerge re-render to prevent ECharts animating from the old structure and
   * producing the "all-blue" glitch.
   */
  private prevSegmentCount = 0;

  private hasBuiltOptions = false;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private readonly themeService = inject(ThemeService);

  ngOnInit(): void {
    if (!this.hasBuiltOptions) {
      this.buildOptions();
    }
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(): void {
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.echartsInstance?.resize();
    });
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  onChartInit(ec: any): void {
    this.echartsInstance = ec;
    this.echartsInstance.resize();
  }

  onChartClick(params: any): void {
    if (typeof params?.dataIndex === 'number' && params.name !== '__gap__') {
      const nativeEvent = params.event?.event ?? params.event;
      if (!nativeEvent) return;
      this.segmentClick.emit({ index: params.dataIndex, nativeEvent });
    }
  }

  onChartContextMenu(params: any): void {
    if (typeof params?.dataIndex === 'number' && params.name !== '__gap__') {
      const nativeEvent = params.event?.event ?? params.event;
      if (!nativeEvent) return;
      nativeEvent.preventDefault?.();
      this.segmentContextMenu.emit({ index: params.dataIndex, nativeEvent });
    }
  }

  onSegmentHover(params: any): void {
    if (params?.dataIndex != null && params.name !== '__gap__') {
      this.segmentHoverIndex.emit(params.dataIndex);
    }
  }

  onSegmentOut(_params: any): void {
    this.segmentHoverIndex.emit(null);
  }

  private buildOptions(): void {
    this.hasBuiltOptions = true;
    const vm = this.viewModel;
    const c = chartColors();

    // ── Derive data from viewModel if present, else fall back to legacy data ──
    let labels: string[] = [];
    let values: number[] = [];
    let colors: string[] = [];
    const hoveredIdx = vm?.hoveredIndex ?? null;

    if (vm && vm.clients.length > 0) {
      labels = vm.clients.map(c => c.clientName);
      values = vm.clients.map(c => c.revenue);
      // Each segment uses its own palette colour assigned by the page-level colorMap.
      colors = vm.clients.map(c => c.color);
    } else if (this.data?.labels?.length) {
      labels = this.data.labels;
      values = this.data.datasets?.[0]?.data ?? [];
      colors = breakdownPalette().slice(0, values.length);
    }

    if (!values.length) {
      this.options = null;
      this.prevSegmentCount = 0;
      return;
    }

    // ── Segment count change detection ───────────────────────────────────────
    // When moving between different numbers of segments (drill-down 10→1,
    // drill-up 1→10) we must use notMerge so ECharts doesn't animate from the
    // old structure — that causes all new segments to inherit the previous
    // segment's colour (the "all-blue" bug).
    const newSegmentCount = values.length;
    const countChanged = this.prevSegmentCount !== 0 && this.prevSegmentCount !== newSegmentCount;
    this.prevSegmentCount = newSegmentCount;

    const total = values.reduce((s, v) => s + v, 0) || 1;
    // Gap slice = 28% of total sweep (creates the open lower-left gap)
    const gapValue = (total / (1 - 0.28)) * 0.28;

    const seriesData: any[] = values.map((val, i) => ({
      name: labels[i],
      value: val,
      itemStyle: {
        color: colors[i] ?? c.cardBorder,
        opacity: hoveredIdx !== null && hoveredIdx !== i ? 0.45 : 1,
      },
      emphasis: {
        itemStyle: {
          color: colors[i] ?? c.cardBorder,
          opacity: 1,
          shadowBlur: 6,
          shadowColor: c.accent,
        },
        scale: false,
      },
    }));

    seriesData.push({
      name: '__gap__',
      value: gapValue,
      itemStyle: { color: 'none', borderWidth: 0 },
      emphasis: { disabled: true },
      tooltip: { show: false },
    });

    const fmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

    const newOptions: EChartsOption = {
      tooltip: {
        show: true,
        backgroundColor: c.chartSurface,
        borderColor: c.cardBorder,
        borderWidth: 1,
        padding: [6, 10],
        textStyle: { color: c.textPrimary, fontSize: 11 },
        formatter: (params: any) => {
          if (params.name === '__gap__') return '';
          const pct = ((params.value / total) * 100).toFixed(1);
          return `<b>${params.name}</b><br/>${fmt.format(params.value)}&nbsp;<span style="color:${c.muted}">(${pct}%)</span>`;
        },
      },
      series: [{
        type: 'pie',
        radius: ['56%', '82%'],
        center: ['50%', '50%'],
        startAngle: 215,
        label: { show: false },
        labelLine: { show: false },
        data: seriesData,
        cursor: 'pointer',
        itemStyle: { borderWidth: 3, borderColor: c.cardBg },
      }],
    };

    if (countChanged && this.echartsInstance) {
      // Segment count changed — bypass ngx-echarts and call setOption directly
      // with notMerge: true so ECharts fully re-renders without animating from
      // the old (wrong-colour) structure.
      this.echartsInstance.setOption(newOptions, { notMerge: true });
      // Also update this.options so ngx-echarts stays in sync for the next
      // hover-only update (which goes through the normal [options] binding).
      this.options = newOptions;
    } else {
      // Normal update (hover opacity change, same segment count):
      // let ngx-echarts handle it via [options] binding for smooth transitions.
      this.options = newOptions;
    }
  }
}
